// =====================================================================================
// INVRT Radar - web console - GENERATED Campaign Reference data (story P2-UI)
//
// GENERATED FILE - do not edit by hand. Regenerate with:
//   node scripts/generateCampaignReference.mjs
// from scripts/source/INVRT_Campaign_Reference_2026-09-15.md (the vendored, byte-identical
// copy of "INVRT Intent Engine - Campaign Reference", generated 2026-09-15 from the live
// database). Every figure below is the document's own; overview and strategy copy is as
// stored, verbatim. The vitest suite re-hashes the source md against SOURCE_SHA256 below.
// =====================================================================================

export interface CampaignReferenceSetting {
  label: string;
  value: string;
}

export interface CampaignReferenceVolume {
  batches: number;
  analysed: number;
  validLeads: number;
  reIntentEvents: number;
  firstBatch: string;
  lastBatch: string;
  /** The document's own volume sentence, verbatim. */
  sentence: string;
}

export interface CampaignReferenceEntry {
  /** The section heading in the reference document. */
  name: string;
  /** The campaign_key, e.g. "smarta_chargeback". */
  key: string;
  client: string;
  /** The document's provenance note for the client, e.g. "from the campaign record". */
  clientSource: string;
  /** "B2B" or "B2C". */
  type: string;
  reach: string;
  created: string;
  /** Present only where the document records a shared campaign, e.g. solar. */
  sharedCampaign: string | null;
  /** The document's status line, e.g. "active; AI analysis enabled; quality threshold 50". */
  statusLine: string;
  qualityThreshold: number | null;
  analysisEnabled: boolean;
  /** As stored, verbatim. */
  overview: string;
  /** As stored, verbatim. */
  strategy: string;
  searchTerms: string[];
  settings: CampaignReferenceSetting[];
  volume: CampaignReferenceVolume;
}

export const CAMPAIGN_REFERENCE_GENERATED_FROM = {
  sourceFile: "scripts/source/INVRT_Campaign_Reference_2026-09-15.md",
  documentDate: "2026-09-15",
  sourceSha256: "abe89b2c93cffa86206cd48fe647eb637b1543615b4ecaf699ae33ba158af9ef",
  generator: "scripts/generateCampaignReference.mjs",
} as const;

export const CAMPAIGN_REFERENCE_TAG_LEGEND = "Tag A = highest-fit / strongest signal set, Tag B = qualified secondary (including \"scout\" researchers inside a target account), Tag C = peripheral. \"HOT\" = a lead carrying 3 or more re-intent signal events. Re-intent signal events are repeat search events by the same resolved person, never counted as separate leads.";

export const CAMPAIGN_REFERENCE_DATA_NOTES: readonly string[] = [
  "Every campaign is currently marked active with AI analysis enabled.",
  "`qualification_filters` is empty on all 12 campaigns, so the campaign-level `quality_threshold` is the only quality gate in force.",
  "No ad-account identifiers (Meta, Google, TikTok, LinkedIn) are stored on any campaign; audience files are produced as CSVs and uploaded manually into each ad platform.",
  "Two campaigns enumerate all 50 states + DC in their territory rules (UAE Real Estate, UAE Company Formation). That is functionally national US reach, written out explicitly rather than left blank.",
  "`solar` is national, with no state filter (Mark's ruling, story W-B3). No campaign carries a regional restriction; the earlier Florida note for the Urban Solar organisation no longer applies.",
  "Campaign-level and client-level export toggles can disagree (e.g. Portugal Golden Visa, RB2B, Regenlys enable LinkedIn at client level only). Export generation reads the client-level record."
];

export const CAMPAIGN_REFERENCE_TOTALS = {
  campaigns: 12,
  batches: 172,
  validLeads: 6213996,
  reIntentEvents: 5336310,
} as const;

export const CAMPAIGN_REFERENCE: CampaignReferenceEntry[] = [
  {
    "name": "Smarta Chargeback",
    "key": "smarta_chargeback",
    "client": "Smarta Payments",
    "clientSource": "from the client configuration record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2026-05-14",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 50",
    "qualityThreshold": 50,
    "analysisEnabled": true,
    "overview": "Findinh HIGH-INTENT at risk merchants",
    "strategy": "Campaign Strategy: Smarta Payments / Chargeback.io -\n\nThe AI targets US-based E-commerce Founders, CFOs, and RevOps leaders at high-risk or high-growth merchants (SaaS, Subscriptions, iGaming, D2C). It identifies active search intent for Visa VAMP compliance (1.5% threshold), RDR/CDRN automation, and network-direct alerts to find merchants in acute \"Payment Crisis.\" The AI must prioritize \"Switcher\" intent and \"Network-Aware\" queries, positioning Smarta as the zero-contract, no-duplicate-billing safeguard that intercepts disputes before they trigger merchant account shutdowns or fund holds.\n\nStrategic Logic for AI Lead Analysis:\n\nThe \"Radar Effect\" Weighting: AI must identify \"Staff\" level researchers (e.g., Marketing/Support) as \"Scouts\" (Category B) for the C-suite. High-intent queries from these profiles signal a directive from the top to solve a payment crisis.\n\nCompetitor Intercept (The Switcher): Prioritize queries comparing Smarta to Chargeflow, Disputifier, or Chargeblast. These leads are already problem-aware but seeking Smarta’s specific USPs: cheapest published rates ($15/$29) and no duplicate billing.\n\nAcute Pain Triggers: Prioritize leads searching for \"Stripe rolling reserve,\" \"VAMP 1.5% compliance,\" or \"acquirer remediation.\" These are \"HOT\" leads (3+ signals) requiring immediate human-in-the-loop intervention.\n\nIdentity Resolution (IDR) Bridge: Use IDR search data to find leaders wanting to deanonymize website traffic to identify high-risk \"Friendly Fraud\" actors at the person-level before they checkout.",
    "searchTerms": [
      "Visa VAMP 1.5 percent threshold",
      "merchant account remediation plan",
      "stop Shopify payments rolling reserve",
      "RDR vs CDRN for Visa",
      "Ethoca alert pricing for Stripe",
      "Chargeflow alternatives for high risk",
      "Disputifier vs Chargeblast cost",
      "prevent friendly fraud for subscriptions",
      "reduce Stripe dispute ratio fast",
      "Visa VAMP Excessive Merchant status",
      "Verifi RDR setup for Braintree",
      "Ethoca alerts for Recurly",
      "smarta payments chargeback calculator",
      "stop Chargebee chargeback fees",
      "Mastercard QMAP threshold 2026",
      "Chargebacks911 alternative for SMB",
      "prevent Airwallex fund holds",
      "Stripe Radar vs Chargeback.io",
      "automate dispute refunds Shopify",
      "high volume dropshipping chargeback solution",
      "person-level visitor tracking for fraud",
      "reveal anonymous website traffic for chargebacks",
      "US website visitor identification for payment risk",
      "unmask anonymous B2B visitors RevOps",
      "B2B identity resolution for account protection",
      "identify fraudulent website visitors real time",
      "deanonymize US merchant traffic",
      "sales intelligence for payment processing risk",
      "Chargeback.io reviews Ganim brothers",
      "official Ethoca and Verifi partner cost."
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok, LinkedIn"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, LinkedIn"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 15,
      "analysed": 15,
      "validLeads": 460466,
      "reIntentEvents": 450336,
      "firstBatch": "2026-06-10",
      "lastBatch": "2026-08-31",
      "sentence": "15 batches uploaded, 15 fully analysed, 460,466 valid leads, 450,336 re-intent signal events, first batch 2026-06-10, most recent 2026-08-31."
    }
  },
  {
    "name": "High-Risk Merchant Accounts",
    "key": "smarta_payments",
    "client": "Smarta Payments",
    "clientSource": "from the campaign record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2025-12-07",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 50",
    "qualityThreshold": 50,
    "analysisEnabled": true,
    "overview": "Merchant account solutions",
    "strategy": "Smarta Payments targets small-to-medium businesses and e-commerce operators seeking modern payment processing solutions with competitive rates and seamless integration. The ideal customer is processing $10K-$500K monthly in transactions and looking to reduce processing fees, improve checkout conversion, or consolidate payment infrastructure. Campaign focuses on business owners and CFOs evaluating payment platform alternatives.",
    "searchTerms": [
      "payment processing",
      "merchant services",
      "pos system",
      "credit card processing",
      "payment gateway",
      "point of sale",
      "card reader",
      "mobile payments",
      "contactless payments",
      "payment terminal",
      "merchant account",
      "square alternative",
      "stripe competitor",
      "clover pos",
      "toast pos",
      "shopify payments",
      "woocommerce payments",
      "payment facilitator",
      "payfac",
      "iso merchant services",
      "high risk merchant",
      "cbd payment processing",
      "cannabis payments",
      "subscription billing",
      "recurring payments",
      "invoicing software",
      "accounts receivable",
      "payment automation",
      "b2b payments",
      "virtual terminal"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok, LinkedIn"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok, LinkedIn"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 1,
      "analysed": 1,
      "validLeads": 75717,
      "reIntentEvents": 74054,
      "firstBatch": "2026-05-18",
      "lastBatch": "2026-05-18",
      "sentence": "1 batches uploaded, 1 fully analysed, 75,717 valid leads, 74,054 re-intent signal events, first batch 2026-05-18, most recent 2026-05-18."
    }
  },
  {
    "name": "UAE Real Estate & Golden Visa",
    "key": "elyora_uae_realestate",
    "client": "Elyora",
    "clientSource": "from the campaign record",
    "type": "B2C",
    "reach": "National (US) - all 51 states + DC explicitly enumerated",
    "created": "2026-03-15",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 45",
    "qualityThreshold": 45,
    "analysisEnabled": true,
    "overview": "Dubai real estate and residency",
    "strategy": "We are looking for decision makers that are wanting investment property in the UAE< this could be for the tax benefits of the Golden Visa, pre-retirement multi-country living.. minimise tax, or just as an investment... They also may be researching personally as they in the process of planning to set up a business in the UAE/Move to the UAE...",
    "searchTerms": [
      "dubai real estate",
      "buy property dubai",
      "dubai apartment",
      "palm jumeirah",
      "dubai marina apartment",
      "downtown dubai property",
      "dubai villa",
      "emaar properties",
      "dubai off plan",
      "dubai property investment",
      "uae real estate",
      "dubai luxury homes",
      "dubai waterfront",
      "business bay apartment",
      "jbr apartment",
      "dubai hills estate",
      "arabian ranches",
      "dubai property visa",
      "golden visa dubai property",
      "dubai rental yield",
      "dubai freehold",
      "dubai property developer",
      "dubai real estate agent",
      "buy apartment uae",
      "dubai property market",
      "invest in dubai",
      "dubai second home",
      "middle east property",
      "gcc real estate",
      "dubai property expo"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - all 51 states + DC explicitly enumerated"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "personal_verified, skiptrace, personal, business_verified"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 40,
      "analysed": 40,
      "validLeads": 1229518,
      "reIntentEvents": 1489637,
      "firstBatch": "2026-03-15",
      "lastBatch": "2026-08-31",
      "sentence": "40 batches uploaded, 40 fully analysed, 1,229,518 valid leads, 1,489,637 re-intent signal events, first batch 2026-03-15, most recent 2026-08-31."
    }
  },
  {
    "name": "UAE Company Formation",
    "key": "elyora_uae_company",
    "client": "Elyora",
    "clientSource": "from the campaign record",
    "type": "B2B",
    "reach": "National (US) - all 51 states + DC explicitly enumerated",
    "created": "2026-03-15",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 45",
    "qualityThreshold": 45,
    "analysisEnabled": true,
    "overview": "Dubai business incorporation",
    "strategy": "We are looking for decision makers that are wanting to set-up a company in the UAE, or maybe looking to move to the UAE for someone else... it may also fall into the sector of them researching investment property in the UAE, and linking company, visa, with investment property.",
    "searchTerms": [
      "dubai company formation",
      "uae free zone",
      "dubai business setup",
      "uae corporate bank",
      "offshore company dubai",
      "dubai mainland company",
      "emirates company",
      "uae business license",
      "dubai trade license",
      "jafza company",
      "dmcc company",
      "rakez free zone",
      "uae holding company",
      "dubai corporate services",
      "uae nominee director",
      "dubai visa company",
      "uae substance",
      "dubai offshore",
      "uae tax residency",
      "dubai crypto company",
      "uae fintech license",
      "dubai trading company",
      "import export dubai",
      "uae llc formation",
      "dubai professional license",
      "uae virtual office",
      "dubai registered agent",
      "emirates business bank",
      "uae company secretary",
      "dubai corporate tax"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - all 51 states + DC explicitly enumerated"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 12,
      "analysed": 12,
      "validLeads": 556443,
      "reIntentEvents": 570384,
      "firstBatch": "2026-03-15",
      "lastBatch": "2026-03-18",
      "sentence": "12 batches uploaded, 12 fully analysed, 556,443 valid leads, 570,384 re-intent signal events, first batch 2026-03-15, most recent 2026-03-18."
    }
  },
  {
    "name": "Portugal Golden Visa",
    "key": "elyora_portugal_visa",
    "client": "Elyora",
    "clientSource": "from the campaign record",
    "type": "B2C",
    "reach": "National (US) - no state filter applied",
    "created": "2026-03-15",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 45",
    "qualityThreshold": 45,
    "analysisEnabled": true,
    "overview": "Portugal residency through investment",
    "strategy": "RifTrust Portugal Golden Visa campaign targets high-net-worth individuals and families seeking European residency through Portugal investment immigration programs. The customer profile includes entrepreneurs, investors, and professionals with $500K+ liquid assets exploring relocation, tax optimization, and EU citizenship pathways. Campaign prioritizes leads showing intent around Portugal real estate, EU residency, and wealth migration.",
    "searchTerms": [
      "portugal golden visa",
      "portugal d7 visa",
      "digital nomad visa",
      "eu residency",
      "move to europe",
      "portugal residency",
      "portuguese citizenship",
      "retire in portugal",
      "portugal expat",
      "lisbon real estate",
      "algarve property",
      "portugal investment visa",
      "non habitual resident",
      "nhr portugal",
      "portugal tax benefits",
      "live in portugal",
      "portugal immigration",
      "europe relocation",
      "portugal visa requirements",
      "golden visa europe",
      "portugal passport",
      "eu citizenship by investment",
      "portugal startup visa",
      "second residency",
      "plan b residency",
      "offshore residency",
      "tax optimization europe",
      "retire abroad",
      "expat tax planning",
      "wealth migration"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok, LinkedIn"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "personal_verified, skiptrace, personal, business_verified"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 2,
      "analysed": 2,
      "validLeads": 48835,
      "reIntentEvents": 28857,
      "firstBatch": "2026-01-07",
      "lastBatch": "2026-01-07",
      "sentence": "2 batches uploaded, 2 fully analysed, 48,835 valid leads, 28,857 re-intent signal events, first batch 2026-01-07, most recent 2026-01-07."
    }
  },
  {
    "name": "Aus Real Estate",
    "key": "aus_real_estate",
    "client": "Accrue",
    "clientSource": "from the client configuration record",
    "type": "B2C",
    "reach": "National (US) - no state filter applied",
    "created": "2026-03-22",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 50",
    "qualityThreshold": 50,
    "analysisEnabled": true,
    "overview": "Australian Investment property advisors looking for people in the US actively searching for and researching investment property in Australia",
    "strategy": "Australian Investment property advisors looking for people in the US actively searching for and researching investment property in Australia",
    "searchTerms": [
      "FIRB approval for US citizens",
      "residential FIRB application fees",
      "Foreign Investment Review Board guidelines 2026",
      "Australian property tax for US residents",
      "negative gearing for non-residents",
      "Australian property portfolio management",
      "buyers agent Australia for US investors",
      "high yield Melbourne residential investment",
      "capital growth suburbs Melbourne 2026",
      "safe haven real estate outside USA",
      "SMSF property investment Australia from US",
      "non-resident Australian mortgage for US citizens",
      "Australian land tax for foreign owners",
      "transfer USD to AUD for property",
      "Australian residential property investment for expats",
      "US expat tax on Australian rental income",
      "off-market Australian property for foreign buyers",
      "Accrue Real Estate reviews",
      "Jeff Grochowski property strategy",
      "Australian property market forecast for US investors",
      "FIRB compliant new dwellings",
      "Australian capital gains tax for US citizens",
      "professional property management Australia remote",
      "Australian real estate investment advisory",
      "diversifying US portfolio into Australian property",
      "investment property concierge Australia",
      "FIRB vacancy fee return",
      "Australian stamp duty surcharge for foreign buyers",
      "luxury residential investment Australia",
      "Australian property asset protection US investors"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "personal_verified, skiptrace, personal, business_verified"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 23,
      "analysed": 23,
      "validLeads": 705520,
      "reIntentEvents": 321550,
      "firstBatch": "2026-03-23",
      "lastBatch": "2026-08-31",
      "sentence": "23 batches uploaded, 23 fully analysed, 705,520 valid leads, 321,550 re-intent signal events, first batch 2026-03-23, most recent 2026-08-31."
    }
  },
  {
    "name": "Identity Resolution",
    "key": "rb2b_idr",
    "client": "RB2B",
    "clientSource": "from the campaign record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2026-01-16",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 40",
    "qualityThreshold": 40,
    "analysisEnabled": true,
    "overview": "Specific Targeting of IDR Searches",
    "strategy": "Specific Targeting of IDR Searches, trying to find decisions makers in business that want to stay ahead of the curve, looking for account based lead tracking on their site and or identify leads visiting their site that are not converting to forms, etc.",
    "searchTerms": [
      "Leadfeeder alternative",
      "Clearbit Reveal pricing",
      "website visitor identification software",
      "Lead Forensics competitors",
      "Warmly vs RB2B",
      "6sense alternatives for SMB",
      "identify anonymous website visitors",
      "ZoomInfo WebSights cost",
      "person-level visitor tracking",
      "Snitcher alternatives",
      "deanonymize website traffic",
      "Visitor Queue reviews",
      "Albacross pricing",
      "reverse IP lookup tools",
      "B2B intent data providers",
      "LinkedIn visitor tracking",
      "Demandbase competitors",
      "HubSpot visitor tracking integration",
      "Koala platform alternatives",
      "automated sales prospecting tools",
      "Factors.ai alternatives",
      "B2B lead generation software for startups",
      "Slack sales alerts",
      "Clay enrichment tools",
      "sales intelligence software",
      "reveal website visitors",
      "account based marketing tools for small business",
      "Kickfire alternatives",
      "US website visitor tracking",
      "real time lead generation"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok, LinkedIn"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 33,
      "analysed": 33,
      "validLeads": 322183,
      "reIntentEvents": 259392,
      "firstBatch": "2026-03-04",
      "lastBatch": "2026-08-31",
      "sentence": "33 batches uploaded, 33 fully analysed, 322,183 valid leads, 259,392 re-intent signal events, first batch 2026-03-04, most recent 2026-08-31."
    }
  },
  {
    "name": "Sentiance Telematics",
    "key": "sentiance_telematics",
    "client": "Sentiance",
    "clientSource": "from the campaign record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2026-01-12",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 50",
    "qualityThreshold": 50,
    "analysisEnabled": true,
    "overview": "This high-intent campaign utilizes a precision-engineered list of 30 \"builder\" and \"buyer\" keywords to exclusively target institutional decision-makers-such as Insurance Product Heads and Mobility CTOs-who are actively researching alternatives to hardware-heavy telematics. By prioritizing technical specificity (e.g., \"mobile telematics SDK,\" \"crash detection API\") and competitive displacement terms (e.g., \"Zendrive alternative\"), the strategy filters out consumer noise to capture mid-to-bottom funnel leads ready for immediate engagement. The engine positions Sentiance’s \"on-device\" edge AI as the superior, privacy-first solution for reducing loss ratios and accelerating time-to-market, ensuring your automated SDRs focus solely on prospects with confirmed intent and budget.",
    "strategy": "This high-intent campaign utilizes a precision-engineered list of 30 \"builder\" and \"buyer\" keywords to exclusively target institutional decision-makers-such as Insurance Product Heads and Mobility CTOs-who are actively researching alternatives to hardware-heavy telematics. By prioritizing technical specificity (e.g., \"mobile telematics SDK,\" \"crash detection API\") and competitive displacement terms (e.g., \"Zendrive alternative\"), the strategy filters out consumer noise to capture mid-to-bottom funnel leads ready for immediate engagement. The engine positions Sentiance’s \"on-device\" edge AI as the superior, privacy-first solution for reducing loss ratios and accelerating time-to-market, ensuring your automated SDRs focus solely on prospects with confirmed intent and budget. Sentiance targets UBI, fleet, and gig economy verticals, providing AI-driven mobile telematics (SDK/API) for driver behavior scoring, crash detection, and claims automation to reduce insurance loss ratios.",
    "searchTerms": [
      "mobile telematics sdk",
      "crash detection api",
      "usage based insurance platform providers",
      "Zendrive alternative",
      "Cambridge Mobile Telematics competitors",
      "transport mode detection api",
      "driver behavior scoring model",
      "smartphone sensor fusion algorithms",
      "reduce auto insurance loss ratio",
      "white label driver safety app",
      "telematics data for claims automation",
      "distracted driving detection sdk",
      "The Floow alternatives",
      "IMS One App alternative",
      "driver risk profiling software",
      "battery efficient location tracking",
      "app-based fleet tracking no hardware",
      "automotive data monetization strategy",
      "eco-driving score api",
      "Arity pricing",
      "last mile delivery safety solutions",
      "automated accident reconstruction",
      "privacy first telematics",
      "UBI program implementation cost",
      "mobile telematics sdk android",
      "telematics sdk ios",
      "smartphone telematics vs obd",
      "fleet driver coaching app",
      "react native telematics library",
      "best telematics for gig economy"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 16,
      "analysed": 16,
      "validLeads": 378786,
      "reIntentEvents": 474943,
      "firstBatch": "2026-03-04",
      "lastBatch": "2026-04-19",
      "sentence": "16 batches uploaded, 16 fully analysed, 378,786 valid leads, 474,943 re-intent signal events, first batch 2026-03-04, most recent 2026-04-19."
    }
  },
  {
    "name": "AI Systems & CRM",
    "key": "invrt_ai_systems",
    "client": "INVRT Marketing",
    "clientSource": "from the campaign record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2025-12-07",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 45",
    "qualityThreshold": 45,
    "analysisEnabled": true,
    "overview": "Sales automation and CRM",
    "strategy": "INVRT AI Systems targets technology-forward enterprises seeking AI-powered operational intelligence, predictive analytics, and automation solutions. The ideal customer is a mid-to-large organization with existing data infrastructure looking to accelerate decision-making through machine learning and AI integration. Campaign focuses on C-suite and VP-level technology leaders evaluating enterprise AI platforms.",
    "searchTerms": [
      "salesforce",
      "hubspot",
      "crm software",
      "ai sales agent",
      "sales automation",
      "lead management",
      "pipeline management",
      "sales intelligence",
      "revenue operations",
      "sales engagement",
      "outreach automation",
      "sales ai",
      "conversation intelligence",
      "gong alternative",
      "chorus ai",
      "salesloft",
      "outreach io",
      "apollo io",
      "zoominfo",
      "linkedin sales navigator",
      "sales enablement",
      "crm implementation",
      "salesforce consulting",
      "hubspot partner",
      "marketo",
      "pardot",
      "sales forecasting",
      "deal intelligence",
      "buyer intent data",
      "sales productivity"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 17,
      "analysed": 17,
      "validLeads": 1350949,
      "reIntentEvents": 1064612,
      "firstBatch": "2025-12-07",
      "lastBatch": "2026-08-17",
      "sentence": "17 batches uploaded, 17 fully analysed, 1,350,949 valid leads, 1,064,612 re-intent signal events, first batch 2025-12-07, most recent 2026-08-17."
    }
  },
  {
    "name": "Marketing & Content Creation",
    "key": "invrt_marketing",
    "client": "INVRT Marketing",
    "clientSource": "from the campaign record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2025-12-07",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 45",
    "qualityThreshold": 45,
    "analysisEnabled": true,
    "overview": "Content production and marketing",
    "strategy": "INVRT Marketing campaign targets digital marketing agencies, brand strategists, and marketing directors seeking advanced audience intelligence and intent-driven campaign optimization. The goal is to demonstrate how behavioral data and AI-powered insights can transform advertising ROI across Meta, Google, and TikTok platforms. Focus on decision-makers managing $50K+ monthly ad spend.",
    "searchTerms": [
      "marketing agency",
      "advertising campaign",
      "google ads",
      "meta ads",
      "facebook ads",
      "digital marketing",
      "ppc management",
      "social media marketing",
      "content marketing",
      "seo services",
      "email marketing",
      "marketing automation",
      "demand generation",
      "lead generation",
      "brand awareness",
      "performance marketing",
      "programmatic advertising",
      "display advertising",
      "youtube ads",
      "tiktok ads",
      "influencer marketing",
      "affiliate marketing",
      "conversion optimization",
      "landing page optimization",
      "marketing analytics",
      "attribution modeling",
      "customer acquisition",
      "growth marketing",
      "b2b marketing",
      "account based marketing"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 7,
      "analysed": 7,
      "validLeads": 1036105,
      "reIntentEvents": 555110,
      "firstBatch": "2026-08-14",
      "lastBatch": "2026-08-17",
      "sentence": "7 batches uploaded, 7 fully analysed, 1,036,105 valid leads, 555,110 re-intent signal events, first batch 2026-08-14, most recent 2026-08-17."
    }
  },
  {
    "name": "regenlys",
    "key": "regenlys___bandages",
    "client": "Carroll",
    "clientSource": "from the client configuration record",
    "type": "B2B",
    "reach": "National (US) - no state filter applied",
    "created": "2026-01-31",
    "sharedCampaign": null,
    "statusLine": "active; AI analysis enabled; quality threshold 50",
    "qualityThreshold": 50,
    "analysisEnabled": true,
    "overview": "A Campaign looking for buyers, procurement officers of Govt, Semi Govt and Private health providers.",
    "strategy": "A Campaign looking for buyers, procurement officers of Govt, Semi Govt and Private health providers. Targeting DHA, VA, and Tier-1 IDNs, the AI machine captures high-intent procurement signals (SDVOSB, ITAR, TCCC) for burn and ulcer care. It automates technical qualification and evidence-based nurture, bypassing traditional sales friction to secure high-value B2B/GovCon contracts.",
    "searchTerms": [
      "DHA medical contract",
      "VA wound care procurement",
      "SDVOSB medical distributor",
      "ITAR compliant dressings",
      "TCCC approved dressings",
      "DLA troop support medical",
      "military burn dressings",
      "combat trauma hydrogel",
      "CAGE code medical",
      "MIL-SPEC wound care",
      "FEMA AEL trauma",
      "EMS burn protocols",
      "municipal medical tender",
      "grant-funded medical supplies",
      "emergency medical procurement",
      "public safety hydrogel",
      "medical disaster response",
      "state health procurement",
      "first responder dressings",
      "tactical EMS supplies",
      "GPO wound care",
      "IDN medical contract",
      "hospital value analysis",
      "Mepilex alternative price",
      "Aquacel alternative specs",
      "enterprise medical pricing",
      "chronic wound ROI",
      "clinical dressing trials",
      "bioactive alginate cost",
      "surgical hydrogel contract",
      "SDVOSB medical procurement",
      "combat wound dressing",
      "tactical burn care",
      "diabetic foot ulcer treatments",
      "bioactive hydrogel",
      "opioid-sparing wound care",
      "DLA medical catalog",
      "veteran owned distributor",
      "prolonged field care",
      "large surface burn dressing",
      "chronic leg ulcer products",
      "non-adherent dressing",
      "rapid hemostasis",
      "infection control bandages",
      "hospital supply chain resilience",
      "biodegradable wound care",
      "mass casualty supplies",
      "advanced wound matrix",
      "EMS trauma kits",
      "TCCC compliant gear",
      "pressure injury prevention",
      "surgical wound healing",
      "Ukraine battlefield tested",
      "VA medical contracts",
      "analgesic wound dressing",
      "transparent burn mask",
      "alginate hydrogel hybrid",
      "emergency medical logistics",
      "autolytic debridement products",
      "domestic medical manufacturing"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok, LinkedIn"
      },
      {
        "label": "Territory rules",
        "value": "National (US) - no state filter applied"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "business_verified, personal_verified, business, personal"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 5,
      "analysed": 5,
      "validLeads": 48244,
      "reIntentEvents": 47435,
      "firstBatch": "2026-03-20",
      "lastBatch": "2026-03-22",
      "sentence": "5 batches uploaded, 5 fully analysed, 48,244 valid leads, 47,435 re-intent signal events, first batch 2026-03-20, most recent 2026-03-22."
    }
  },
  {
    "name": "Solar Panel Installation",
    "key": "solar",
    "client": "Urban Solar / Energy4u",
    "clientSource": "from the client configuration record",
    "type": "B2C",
    "reach": "National",
    "created": "2025-12-07",
    "sharedCampaign": "run for Urban Solar, Energy4u",
    "statusLine": "active; AI analysis enabled; quality threshold 40",
    "qualityThreshold": 40,
    "analysisEnabled": true,
    "overview": "Residential solar panel installation",
    "strategy": "We are looking for people that are really in the buying mode, heading towards the bottom of the funnel when it comes to buying and/or updating/upgrading their residential solar in their house. Could be first time to solar and not sure, could be with solar but not batteries!!! Or they could be researching as they have been speaking with a competitor... or a competitor they were with is no out of business and they need someone to maintain their system.",
    "searchTerms": [
      "solar panels",
      "solar installation",
      "home solar",
      "solar quotes",
      "residential solar",
      "solar energy system",
      "rooftop solar",
      "solar panel cost",
      "solar financing",
      "solar savings",
      "solar battery",
      "solar inverter",
      "net metering",
      "solar lease",
      "solar ppa",
      "go solar",
      "solar tax credit",
      "itc solar",
      "clean energy home",
      "renewable energy residential",
      "solar roof",
      "tesla solar",
      "sunrun",
      "vivint solar",
      "solar panel installation near me",
      "best solar companies",
      "solar panel efficiency",
      "how much do solar panels cost",
      "solar energy savings calculator",
      "home battery storage"
    ],
    "settings": [
      {
        "label": "Campaign-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Client-level export platforms",
        "value": "Meta, Google, TikTok"
      },
      {
        "label": "Territory rules",
        "value": "National"
      },
      {
        "label": "Qualification filters",
        "value": "{}"
      },
      {
        "label": "Max leads per batch",
        "value": "no cap"
      },
      {
        "label": "Priority order",
        "value": "1"
      },
      {
        "label": "Email priority used",
        "value": "personal_verified, skiptrace, personal, business_verified"
      },
      {
        "label": "Ad-account IDs on file",
        "value": "Meta [], Google none, TikTok none, LinkedIn page none"
      }
    ],
    "volume": {
      "batches": 1,
      "analysed": 0,
      "validLeads": 1230,
      "reIntentEvents": 0,
      "firstBatch": "2025-12-07",
      "lastBatch": "2025-12-07",
      "sentence": "1 batches uploaded, 0 fully analysed, 1,230 valid leads, 0 re-intent signal events, first batch 2025-12-07, most recent 2025-12-07."
    }
  }
];

/**
 * HIGH-INTENT proxy campaign codes use internal hyphens ("rb2b-idr"); the reference
 * document's campaign_key uses underscores ("rb2b_idr"). One normalisation, used by
 * every lookup, so the two vocabularies cannot drift apart silently.
 */
export function normaliseCampaignKey(code: string): string {
  return code.trim().toLowerCase().replaceAll("-", "_");
}

/** The reference entry for a campaign code or key, or undefined when the document has none. */
export function campaignReferenceForCode(
  code: string | null | undefined,
): CampaignReferenceEntry | undefined {
  if (code === null || code === undefined) return undefined;
  const key = normaliseCampaignKey(code);
  if (key === "") return undefined;
  return CAMPAIGN_REFERENCE.find((entry) => entry.key === key);
}
